import { useState } from "react";
import { offerData } from "../data/mock";
import {
  Building2,
  ArrowLeftRight,
  Globe,
  Shield,
  Plane,
  TrendingUp,
  Compass,
  Brain,
  ChevronDown,
  Check,
  ArrowRight,
} from "lucide-react";

const iconComponents = { Building2, ArrowLeftRight, Globe, Shield, Plane, TrendingUp, Compass, Brain };

export const OfferStackSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section id="services" data-testid="services-section" className="relative py-28" style={{ background: "#1a1c1b" }}>
      <div className="absolute top-0 left-0 right-0 h-px bg-[#3f4816]/30" />
      <div className="max-w-[87.5rem] mx-auto px-10">
        <div className="mb-14">
          <span className="inline-block text-[#888680] text-xs font-medium uppercase tracking-[0.2em] mb-5" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
            What's Included
          </span>
          <h2 className="font-black uppercase leading-[0.88] max-w-4xl" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(1.8rem, 1.4rem + 2vw, 3.2rem)", letterSpacing: "-0.02em" }}>
            <span className="text-[#dfddd6]">One partner. </span>
            <span className="text-[#d9fb06]">India + the world.</span>
          </h2>
        </div>

        <div className="flex flex-col gap-2">
          {offerData.services.map((service, i) => {
            const Icon = iconComponents[service.icon];
            const isOpen = openIndex === i;

            return (
              <div key={i} className={`border transition-all duration-300 rounded-sm overflow-hidden ${isOpen ? "border-[#3f4816]/30 bg-[#302f2c]/50" : "border-[#3f4816]/10 bg-[#302f2c]/20 hover:border-[#3f4816]/20"}`}>
                {/* Header */}
                <button onClick={() => toggle(i)} className="w-full flex items-center justify-between px-5 md:px-7 py-5 text-left group">
                  <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
                    <div className={`p-2 md:p-2.5 border rounded-md transition-colors duration-300 flex-shrink-0 ${isOpen ? "border-[#3f4816]/30 bg-[#3f4816]/10" : "border-[#3f4816]/15 group-hover:border-[#3f4816]/25"}`}>
                      {Icon && <Icon size={18} className="text-[#d9fb06]" />}
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-[#d9fb06]/50 text-[10px] font-semibold uppercase tracking-widest mb-1" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
                        {service.tag}
                      </span>
                      <h3 className="text-[#dfddd6] font-bold text-sm md:text-base" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
                        {service.title}
                      </h3>
                      {/* Mobile price - show below title when collapsed */}
                      {service.price && !isOpen && (
                        <span className="md:hidden text-[#888680]/60 text-[10px] mt-1" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
                          {service.price.includes("Starting") ? service.price : `Starts from ${service.price.split(" / ")[0]}`}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 md:gap-4 flex-shrink-0">
                    {service.price && (
                      <span className="hidden md:inline text-[#d9fb06] text-xs font-semibold" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
                        {service.price}
                      </span>
                    )}
                    <ChevronDown size={18} className={`text-[#888680] transition-transform duration-300 flex-shrink-0 ${isOpen ? "rotate-180 text-[#d9fb06]" : ""}`} />
                  </div>
                </button>

                {/* Expanded Content */}
                <div className="overflow-hidden transition-all duration-300" style={{ maxHeight: isOpen ? "700px" : "0", opacity: isOpen ? 1 : 0 }}>
                  <div className="px-7 pb-7 pt-0">
                    <div className="ml-[52px]">
                      {/* Tagline */}
                      <p className="text-[#888680] text-sm leading-relaxed mb-5 italic" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
                        {service.tagline}
                      </p>

                      {/* Includes */}
                      <div className="flex flex-col gap-2 mb-5">
                        {service.includes.map((item, j) => (
                          <div key={j} className="flex items-start gap-2.5">
                            <div className="flex-shrink-0 mt-0.5 w-5 h-5 rounded bg-[#3f4816]/10 flex items-center justify-center">
                              <Check size={11} className="text-[#d9fb06]/70" />
                            </div>
                            <p className="text-[#888680] text-sm leading-relaxed" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
                              {item}
                            </p>
                          </div>
                        ))}
                      </div>

                      {/* Price (mobile) + CTAs */}
                      <div className="pt-4 border-t border-[#3f4816]/15">
                        {service.price && (
                          <p className="text-[#d9fb06] text-sm font-bold mb-4" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
                            {service.price}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-3">
                          {service.ctas.map((cta, k) => {
                            const isFreeCta = cta.label.toLowerCase().includes("free");
                            return isFreeCta ? (
                              <a
                                key={k}
                                href="#final-cta"
                                onClick={(e) => {
                                  e.preventDefault();
                                  document.querySelector("#final-cta")?.scrollIntoView({ behavior: "smooth" });
                                }}
                                className={`group inline-flex items-center gap-2 font-semibold text-xs px-5 py-2.5 rounded-full transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] uppercase tracking-tight ${
                                  cta.primary
                                    ? "bg-[#d9fb06] text-[#1a1c1b] hover:opacity-90"
                                    : "border border-[#d9fb06]/40 text-[#d9fb06] hover:bg-[#d9fb06]/10"
                                }`}
                                style={{ fontFamily: "'Inter', Arial, sans-serif" }}
                              >
                                {cta.label}
                                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                              </a>
                            ) : cta.href && cta.href.startsWith('/') ? (
                              <a
                                key={k}
                                href={cta.href}
                                className={`group inline-flex items-center gap-2 font-semibold text-xs px-5 py-2.5 rounded-full transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] uppercase tracking-tight ${
                                  cta.primary
                                    ? "bg-[#d9fb06] text-[#1a1c1b] hover:opacity-90"
                                    : "border border-[#d9fb06]/40 text-[#d9fb06] hover:bg-[#d9fb06]/10"
                                }`}
                                style={{ fontFamily: "'Inter', Arial, sans-serif" }}
                              >
                                {cta.label}
                                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                              </a>
                            ) : (
                              <a
                                key={k}
                                href={cta.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`group inline-flex items-center gap-2 font-semibold text-xs px-5 py-2.5 rounded-full transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] uppercase tracking-tight ${
                                  cta.primary
                                    ? "bg-[#d9fb06] text-[#1a1c1b] hover:opacity-90"
                                    : "border border-[#d9fb06]/40 text-[#d9fb06] hover:bg-[#d9fb06]/10"
                                }`}
                                style={{ fontFamily: "'Inter', Arial, sans-serif" }}
                              >
                                {cta.label}
                                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
